import { selectPriceMargin, selectSelectedProducts, selectSelectedTreatsProduct } from '@app/store/products/products.selectors';
import { Injectable } from '@angular/core';
import { Product } from '@app/core/models';
import { FetchProductDataFilters } from '@app/core/models/fetch-product-data-filters';
import { ProductService } from '@app/core/services';
import { GTMService, productSavePrices } from '@app/core/services/tracking';
import { ProductCatalogFilterValues } from '@app/shared/components/product-catalog-filters/product-catalog-filters';
import { ProductType, Tool } from '@app/shared/utils';
import { ProductPillar } from '@app/shared/utils/enums/pillar.enum';
import { TerritoryType } from '@app/shared/utils/enums/territoryType';
import {
  filterCatalogProducts,
  filterCatalogProductsRenalDetect,
  filterOutProductsPackagesByType,
  filterProductsByCatalogFilters,
  formatProductsByCategory,
} from '@app/shared/utils/static-helpers/product-filters-helper';
import { getProductsMergedWithNewPrices, getProductMergedWithNewPrices } from '@app/shared/utils/static-helpers/product-helper';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import { AppState } from '@app/store';
import { setAlert, toggleLoader, selectToolInfo } from '@app/store/core';
import { selectIsPriceUploadEnabled, selectIsSolProductsInVetEnable, selectIsTreatsEnabled } from '@app/store/vet';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { RCAlertType } from '@rc/ui';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  getAllProductsFail,
  resetProductsPricesState,
  setFilteredProducts,
  setFilteredProductsSuccess,
  setProductFilters,
  setProductTypeFilters,
  setForcedProductFilters,
  updatePrices,
  resetAllSelectedProducts,
  removePackFromPriceListToUpdate,
  resetPriceListToUpdate,
  getAllProductsSuccess,
  setSelectedProduct,
  getAllProducts,
  resetSelectedProduct,
  addPricesToUpdate,
  removePacksFromPriceListToUpdate,
  setShowPriceListActions,
  setPriceMargin,
} from '../products.actions';
import {
  selectAllProductsSuccess,
  selectFiltersValues,
  selectPriceListToUpdate,
  selectSelectedDryProduct,
  selectSelectedWetProduct,
} from '../products.selectors';
import { calculatePriceByMargin } from '@app/shared/utils/static-helpers/price-list-helper';
import { of } from 'rxjs';
import { selectConsultationPatient } from '@app/store/consultation/consultation.selectors';

@Injectable()
export class ProductsEffects {
  UpdateProductsPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePrices),
      withLatestFrom(
        this.store$.select(selectPriceListToUpdate),
        this.store$.select(selectAllProductsSuccess),
        this.store$.select(selectSelectedDryProduct),
        this.store$.select(selectSelectedWetProduct),
        this.store$.select(selectSelectedTreatsProduct),
        this.store$.select(selectPriceMargin),
        this.store$.select(selectIsPriceUploadEnabled),
      ),
      switchMap(
        ([
          { withTracking, trackingShouldIncludeProductsInformation, updateAllPrices, productsToUpdate },
          priceListToUpdate,
          products,
          selectedDryProduct,
          selectedWetProduct,
          selectedTreatsProduct,
          priceMargin,
          isPriceUploadEnabled,
        ]) => {
          let localList = priceListToUpdate;
          if (productsToUpdate.length) {
            localList = [priceListToUpdate.find((p) => p.pack.ean === productsToUpdate[0].pack.ean)];
          }
          if (isPriceUploadEnabled) {
            localList = calculatePriceByMargin(localList, priceMargin);
          }
          return this.productService.updateClinicPrices(localList).pipe(
            switchMap(() => {
              if (withTracking) {
                let productsIds;
                if (trackingShouldIncludeProductsInformation) {
                  productsIds = localList.map((item) => `${item.productId}+${item.pack.sCode}`);
                }
                this.trackingService.sendInteraction(productSavePrices(productsIds));
              }

              const actions: Action[] = [
                getAllProductsSuccess({ allProductList: getProductsMergedWithNewPrices(products, localList) }),
                setFilteredProducts(),
                setAlert({
                  alert: {
                    message: translateKey('prices_have_been_well_saved_message'),
                    alertType: RCAlertType.SUCCESS,
                    dataQa: 'vs-saved-prices-success-alert',
                  },
                }),
              ];
              if (updateAllPrices) {
                actions.push(resetProductsPricesState());
              } else {
                localList.forEach((p) => actions.push(removePackFromPriceListToUpdate({ productId: p.productId, packEan: p.pack.ean })));
              }
              if (selectedDryProduct) {
                actions.push(
                  setSelectedProduct({
                    selectedProduct: {
                      product: getProductMergedWithNewPrices(selectedDryProduct, localList),
                    },
                    setProductOnly: true,
                  }),
                );
              }
              if (selectedWetProduct) {
                actions.push(
                  setSelectedProduct({
                    selectedProduct: {
                      product: getProductMergedWithNewPrices(selectedWetProduct, localList),
                    },
                    setProductOnly: true,
                  }),
                );
              }
              if (selectedTreatsProduct) {
                actions.push(
                  setSelectedProduct({
                    selectedProduct: {
                      product: getProductMergedWithNewPrices(selectedTreatsProduct, priceListToUpdate),
                    },
                    setProductOnly: true,
                  }),
                );
              }
              return actions;
            }),
            catchError(() => [
              resetProductsPricesState(),
              setAlert({
                alert: {
                  message: translateKey('edit_prices_error_message'),
                  alertType: RCAlertType.ERROR,
                  dataQa: 'vs-edit-prices-error-alert',
                },
              }),
            ]),
          );
        },
      ),
    ),
  );

  GetAllProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAllProducts),
      switchMap((action) => {
        return this.productService.fetchProducts([ProductPillar.VET, ProductPillar.SPTRETAIL], action.tool).pipe(
          map((products): [Product[], FetchProductDataFilters] => {
            return [filterCatalogProducts(products, action.filterParams), action.filterParams];
          }),
          map(([products, filterParams]): [Product[], FetchProductDataFilters] => {
            return [filterOutProductsPackagesByType(products), filterParams];
          }),
          map(([products, filterParams]): [Product[]] => {
            return filterParams?.renalDetectRecommendationPredictionStatus
              ? [
                  filterCatalogProductsRenalDetect(
                    products,
                    filterParams?.renalDetectRecommendationPredictionStatus,
                    filterParams?.lastConsultationPredictionStatus,
                  ),
                ]
              : [products];
          }),
          switchMap(([products]) => {
            return [getAllProductsSuccess({ allProductList: products }), setFilteredProducts(), toggleLoader({ value: false })];
          }),

          catchError(() => [getAllProductsFail(), toggleLoader({ value: false })]),
        );
      }),
    ),
  );

  SetProductFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setProductFilters),
      mergeMap(() => [setFilteredProducts(), resetPriceListToUpdate()]),
    ),
  );

  SetFilteredProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setFilteredProducts),
      withLatestFrom(
        this.store$.select(selectFiltersValues),
        this.store$.select(selectAllProductsSuccess),
        this.store$.select(selectIsSolProductsInVetEnable),
        this.store$.select(selectIsTreatsEnabled),
        this.store$.select(selectSelectedProducts),
      ),
      map(
        ([_, filters, allProducts, enableSolProductsInVet, isTreatsEnabled, selectedProducts]): [Product[], ProductCatalogFilterValues] => {
          return [filterProductsByCatalogFilters(allProducts, filters, enableSolProductsInVet, isTreatsEnabled, selectedProducts), filters];
        },
      ),
      map(([products, filters]): [Record<TerritoryType | string, Product[]>] => [formatProductsByCategory(products, filters.pillar)]),
      switchMap(([products]) => [setFilteredProductsSuccess({ filteredProductList: products })]),
    ),
  );

  FilterProductsOnSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(...[setSelectedProduct, resetSelectedProduct, resetAllSelectedProducts]),
      withLatestFrom(
        this.store$.select(selectSelectedProducts),
        this.store$.select(selectToolInfo),
        this.store$.select(selectIsTreatsEnabled),
        this.store$.select(selectConsultationPatient),
      ),
      switchMap(([, selectedProducts, { tool }, isTreatsEnabled, patient]) => {
        const productTypeFilters = {
          dry: selectedProducts.every((product) => product?.productType !== ProductType.Dry),
          wet: selectedProducts.every((product) => product?.productType !== ProductType.Wet),
          treats: isTreatsEnabled ? selectedProducts.every((product) => product?.productType !== ProductType.Treats) : false,
        };

        if (tool && tool !== Tool.RapidReco && (tool !== Tool.Rationing || patient)) {
          return [setProductTypeFilters(productTypeFilters), setFilteredProducts()];
        } else {
          return [
            setProductTypeFilters(productTypeFilters),
            setForcedProductFilters({ specieCode: selectedProducts.length ? selectedProducts[0]?.speciesCode : null }),
            setFilteredProducts(),
          ];
        }
      }),
    ),
  );

  updateShowPriceListActions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addPricesToUpdate, removePackFromPriceListToUpdate, removePacksFromPriceListToUpdate, setPriceMargin),
      withLatestFrom(this.store$.select(selectPriceListToUpdate), this.store$.select(selectPriceMargin)),
      switchMap(([_, priceListToUpdate, priceMargin]) => {
        const value =
          priceListToUpdate.filter((price) => !price.updateByMargin).length > 1 ||
          (priceListToUpdate.some((price) => price.updateByMargin) && priceMargin != null && priceMargin >= 0);

        return of(setShowPriceListActions({ value }));
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private productService: ProductService,
    private store$: Store<AppState>,
    private trackingService: GTMService,
  ) {}
}
